<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div class="formatted-input-container">
    <div
      class="formatted-input"
      :class="{ 'show-currency': isMoney }">
      <div
        v-if="prefix"
        class="symbol">
        <span>{{ prefix }}</span>
      </div>
      <div
        v-else-if="isMoney"
        class="symbol">
        <span>{{ currencySymbol }}</span>
      </div>
      <input
        v-show="isEditing === false"
        :name="`${name}-readonly`"
        :disabled="disabled"
        :readonly="readonly"
        :class="{
          'form-control': true,
          input: true,
          'is-danger': isDanger,
          'has-suffix': suffix != null,
          'has-prefix': isMoney || prefix,
        }"
        :value="valueFormatted"
        @focus="editValue" />
      <input
        v-show="isEditing === true"
        ref="valueInput"
        v-model="internalValue"
        :type="inputType"
        :step="step"
        :min="min"
        :max="max"
        :name="name"
        :disabled="disabled"
        :readonly="readonly"
        :class="{
          'hide-arrows': !showArrows,
          'form-control': true,
          input: true,
          'is-danger': isDanger,
          'has-suffix': suffix != null,
          'has-prefix': isMoney || prefix,
        }"
        @blur="onBlur" />
      <div
        v-if="showIcon"
        v-tooltip="iconTooltip"
        class="icon mr-2">
        <slot name="icon" />
      </div>
      <div
        v-if="suffix"
        class="suffix"
        :style="suffixCSS"
        @click="$emit('toggle')">
        <small>{{ suffix }}</small>
        <slot name="suffix-icon" />
      </div>
    </div>
    <span
      v-show="integerOnly && !isEmpty && !internalValueIsInteger"
      class="is-danger"
      >{{ $t('messages.formattedInputMustBeInteger') }}</span
    >
    <span
      v-if="showErrorMessage && validationErrors && validationErrors.has(errorName)"
      class="is-danger help"
      >{{ validationErrors.first(errorName) }}</span
    >
  </div>
</template>

<script type="text/babel">
  import numbro from 'numbro';

  import { DASH } from '@/globals';
  import formatNumber from '@/utils/NumberFormatter';
  import MessageCollection from '@/validator/MessageCollection';

  export default {
    props: {
      name: {
        type: String,
        default: 'value',
      },
      defaultValue: {
        type: Number,
        default: 0,
      },
      value: {
        type: Number,
        default: () => this?.defaultValue,
      },
      format: {
        type: String,
        default: '0,0.00',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      validationErrors: {
        type: MessageCollection,
        default: null,
      },
      errorName: {
        // Name of the error in the $validator or validationErrors
        type: String,
        default: null,
        required: false,
      },
      iconName: {
        type: String,
        default: '',
      },
      iconTooltip: {
        type: String,
        default: '',
      },
      isMoney: {
        type: Boolean,
        default: false,
      },
      isPercentage: {
        type: Boolean,
        default: false,
      },
      isPositiveNumber: {
        type: Boolean,
        default: null,
      },
      min: {
        type: Number,
        default: 0,
      },
      max: {
        type: Number,
        default: null,
      },
      showIcon: {
        type: Boolean,
        default: false,
      },
      prefix: {
        type: String,
        default: null,
      },
      suffix: {
        type: String,
      },
      suffixWidth: {
        type: String,
      },
      step: {
        type: Number,
        default: 1,
      },
      showArrows: {
        type: Boolean,
        default: true,
      },
      inputType: {
        type: String,
        default: 'text',
      },
      suffixStyle: {
        type: String,
      },
      integerOnly: {
        type: Boolean,
        default: false,
      },
      customCurrencySymbol: {
        type: String,
        default: null,
        required: false,
      },
      allowEmpty: {
        type: Boolean,
        default: false,
      },
      showErrorMessage: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isEditing: false,
        internalValue: 0,
        lastValidValue: 0,
      };
    },
    computed: {
      currencySymbol() {
        return this.customCurrencySymbol ?? numbro.languageData().currency.symbol;
      },
      valueFormatted() {
        if (this.internalValue === null) {
          return '';
        }
        return this.isPositiveNumber && this.lastValidValue <= 0
          ? DASH
          : formatNumber(this.internalValue, this.format) + (this.isPercentage ? '%' : '');
      },
      suffixCSS() {
        let style = '';
        if (this.suffixWidth != null) {
          style += `width: ${this.suffixWidth};`;
        }
        if (this.suffixStyle != null) {
          style += this.suffixStyle;
        }
        return style || null;
      },
      isEmpty() {
        return this.internalValue == null || this.internalValue?.length === 0;
      },
      internalValueIsInteger() {
        return Number.isInteger(this.internalValue);
      },
      isDanger() {
        const validator = this.$validator?.messageBag?.errors;
        if (this.errorName) {
          return validator?.has(this.errorName) || this.validationErrors?.has(this.errorName);
        }
        return validator?.has(this.name) || this.validationErrors?.has(this.name);
      },
    },
    watch: {
      value(newValue) {
        if (newValue != null && !Number.isNaN(newValue) && (typeof newValue !== 'string' || newValue.trim() !== '')) {
          this.internalValue = this.validate(newValue);
        } else {
          this.internalValue = null;
        }
      },
      internalValue(newValue) {
        if (
          (newValue != null || this.allowEmpty) &&
          newValue !== this.lastValidValue &&
          (newValue === '' || !Number.isNaN(newValue))
        ) {
          this.lastValidValue = this.validate(newValue);
          this.$emit('valueChanged', this.lastValidValue);
        }
      },
    },
    mounted() {
      this.internalValue = this.value;
    },
    methods: {
      editValue() {
        this.$emit('startedEditing');
        this.isEditing = true;
        setTimeout(() => {
          this.$refs.valueInput.focus();
          this.$refs.valueInput.select();
        }, 200);
      },
      onBlur() {
        this.internalValue = this.lastValidValue;
        // Automatically convert to integer if needed
        if (this.integerOnly && !this.internalValueIsInteger) {
          this.internalValue = Math.round(this.internalValue);
        }
        this.isEditing = false;
        this.$emit('blur');
      },
      validate(value) {
        if (value == '' && this.allowEmpty) return null;

        let validated = this.getValidFormat(value);
        if (this.max != null) {
          validated = Math.min(this.max, validated);
        }
        if (this.min != null) {
          validated = Math.max(this.min, validated);
        }
        return validated || 0;
      },
      getValidFormat(value) {
        let newValue = value;
        // Regex function is used because string function are warned by vue watcher and cause error (undefined functions)
        const hasComma = /,/g.test(value);
        if (hasComma) {
          const formattedValue = formatNumber(value, this.format);
          newValue = numbro(formattedValue).value();
        }
        return newValue;
      },
      focus() {
        this.editValue();
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .formatted-input-container {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .formatted-input {
    width: 100%;
    display: flex;
    flex-direction: row;

    &.show-currency {
      input {
        border-radius: 0 0.25rem 0.25rem 0;
      }
    }

    .suffix,
    .symbol {
      display: flex;
      flex-shrink: 1;
      flex-grow: 0;
      align-items: center;
      padding: 0 1rem;
      background: #eceeef;
      border: 1px solid rgba(0, 0, 0, 0.15);
    }

    .symbol {
      border-radius: 0.25rem 0 0 0.25rem;
      border-right-width: 0;
    }

    .suffix {
      border-radius: 0 0.25rem 0.25rem 0;
      border-left-width: 0;
      flex-shrink: 0;
      svg {
        margin-left: 0.5rem;
        stroke: #999;
        fill: #999;
        width: 1rem;
        height: 1rem;
        cursor: pointer;
      }
    }

    .icon {
      margin-left: -3rem;
      display: flex;
      align-items: center;

      svg {
        stroke: #ff0083;
        width: 2.4rem;
        height: 2.4rem;
      }
    }

    input {
      flex-grow: 1;

      &.has-suffix {
        border-radius: 0.25rem 0 0 0.25rem;
      }
      &.has-prefix {
        border-radius: 0 0.25rem 0.25rem 0;
      }
    }

    .hide-arrows::-webkit-inner-spin-button,
    .hide-arrows::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .campaign-daily-cap {
    .formatted-input {
      max-height: 3.8rem;
    }
    .symbol {
      font-size: 1rem;
      padding: 1.2rem 1.2rem;
    }
  }
</style>
